import React from 'react';
import { styled } from 'styletron-react';
import { Modal, ModalHeader, ModalBody } from 'baseui/modal';
import { HeadingSmall, LabelMedium } from 'baseui/typography';
import { StringColumn, CustomColumn } from 'baseui/data-table';
import { Tag } from 'baseui/tag';

// Type assertions to handle BaseUI compatibility issues
const ModalComponent = Modal as any;
const ModalHeaderComponent = ModalHeader as any;
const ModalBodyComponent = ModalBody as any;

import { BaseGrid } from '../BaseGrid';
import type { PropertyAdminEstate, EstateGroupAccess } from '../../hooks/usePropertyAdmin';
import { useT } from '../../i18n/admin';

const DetailsContainer = styled('div', {
  marginTop: '16px',
});

const StateInfo = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
  padding: '16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
});

const InfoItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

interface EstateGroupDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  estate: PropertyAdminEstate | null;
}

export const EstateGroupDetailsModal: React.FC<EstateGroupDetailsModalProps> = ({
  isOpen,
  onClose,
  estate,
}) => {
  const { fmt } = useT();
  if (!estate) return null;

  const formatDate = (dateString: string) => fmt.date(dateString);

  const getDaysRemaining = (dateString: string) => {
    const today = new Date();
    const endDate = new Date(dateString);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining(estate.authorizedUntil);
  const statusColor = daysRemaining <= 30 ? 'negative' : daysRemaining <= 90 ? 'warning' : 'positive';

    // Columns for estate group details table
  const estateGroupColumns = [
    StringColumn({
      title: 'Property',
      maxWidth: 200,
      mapDataToValue: (row: any) => row.name,
    }),
    StringColumn({
      title: 'Estates Count',
      maxWidth: 120,
      mapDataToValue: (row: any) => row.estateCount.toString(),
    }),
    CustomColumn({
      title: 'Authorization Status',
      maxWidth: 150,
      mapDataToValue: (row: any) => row,
      renderCell: ({ value }: { value: EstateGroupAccess }) => (
        <Tag color={value.isAuthorized ? 'positive' : 'negative'}>
          {value.isAuthorized ? 'Authorized' : 'Not Authorized'}
        </Tag>
      ),
    }),
  ];

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      size="large"
    >
      <ModalHeaderComponent>
        {estate.estateName} - Estate Group Details
      </ModalHeaderComponent>
      <ModalBodyComponent>
        <StateInfo>
          <InfoItem>
            <LabelMedium>Authorization Status</LabelMedium>
            <Tag color={estate.canCreatePasses ? 'positive' : 'negative'}>
              {estate.canCreatePasses ? 'Active' : 'Inactive'}
            </Tag>
          </InfoItem>
          <InfoItem>
            <LabelMedium>Authorized Until</LabelMedium>
            <HeadingSmall>{formatDate(estate.authorizedUntil)}</HeadingSmall>
          </InfoItem>
          <InfoItem>
            <LabelMedium>Days Remaining</LabelMedium>
            <Tag color={statusColor as any}>
              {daysRemaining} days
            </Tag>
          </InfoItem>
          <InfoItem>
            <LabelMedium>Estate Group</LabelMedium>
            <HeadingSmall>{estate.estateGroupName}</HeadingSmall>
          </InfoItem>
        </StateInfo>

        <DetailsContainer>
          <HeadingSmall style={{ marginBottom: '16px' }}>
            Estate Group Information
          </HeadingSmall>
          <BaseGrid
            rows={[estate.estateGroup].map(group => ({ id: group.id, data: group }))}
            columns={estateGroupColumns}
            loading={false}
            emptyMessage="No data available"
            searchable={false}
            filterable={false}
          />
        </DetailsContainer>
      </ModalBodyComponent>
    </ModalComponent>
  );
}; 